import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { formatDate, formatDateTime, money } from '../../lib/format.js'
import { useJobOverlay } from '../../lib/job-overlay.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { Button, Spinner, Textarea, toneStyles } from '../ui.js'
import { AddServiceSheet } from './AddServiceSheet.js'
import { Label } from './panes.js'
import {
  serviceIcon,
  serviceStatus,
  serviceTile,
  serviceTone,
  ticketBadge,
  ticketDetail,
  type JobServiceRow,
} from './services.js'
import type { BriefHazards, BriefHistory, BriefTickets, Job } from './types.js'

/**
 * The services on this job.
 *
 * The primary block on the Work tab, and the thing the whole redesign is for. A
 * job at one address is rarely one piece of work: the inspection, the cockroach
 * treatment and the rat stations happen on one visit, are quoted as separate
 * lines, and each produces its own signed report. ServiceM8 could only say
 * "one job, one category", which is why 660 characters of description ended up
 * carrying five jobs' worth of instructions. Decision 53.
 *
 * A row collapsed is what state the work is in; a row open is the brief a
 * technician reads at the gate — the scope, what the last few people to do this
 * work *at this address* wrote down, and the notes on this service. The brief is
 * fetched when the row opens rather than with the job, because five collapsed
 * rows would otherwise pay for five site-wide note searches to draw five lines
 * of text.
 *
 * The legacy checklist is still on the tab, below and labelled as the old
 * system's. Nothing is back-parsed from it into a service.
 */
export function ServicesPane({
  job,
  canEdit,
  isAddingNote,
  onAddNote,
  onRemoveService,
  field = false,
}: {
  job: Job
  canEdit: boolean
  isAddingNote: boolean
  onAddNote: (jobServiceId: string, body: string, done: () => void) => void
  onRemoveService: (jobServiceId: string) => void
  /**
   * The field app's look: white rows with the report one tap away on a button,
   * the brief still behind the row. Decision 93.
   */
  field?: boolean
}) {
  const [open, setOpen] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const services = job.services ?? []
  const done = services.filter((service) => serviceStatus(service).done).length

  return (
    <section aria-label="Services" className={cn('flex flex-col', field ? 'gap-2' : 'gap-2.5')}>
      {field ? (
        <div className="flex items-baseline justify-between gap-2 px-1.5">
          <span className="text-[13px] font-semibold tracking-[0.04em] text-ink-500 uppercase">
            Services
            {services.length > 0 ? ` · ${done} of ${services.length} done` : ''}
          </span>
          {canEdit ? (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="text-[14px] font-semibold text-brand-700"
            >
              Add a service
            </button>
          ) : null}
        </div>
      ) : (
        <div className="flex items-center gap-2.5">
          <Label>Services on this job</Label>
          {services.length > 0 ? (
            <span className="text-[11.5px] text-ink-500">
              {done} of {services.length} done
            </span>
          ) : null}
          {canEdit ? (
            <Button
              size="sm"
              className="ml-auto"
              icon={<Icon name="plus" className="size-3.5" />}
              onClick={() => setAdding(true)}
            >
              Add a service
            </Button>
          ) : null}
        </div>
      )}

      {services.length === 0 ? (
        <p
          className={cn(
            'text-ink-500',
            field
              ? 'rounded-lg bg-white px-4 py-4 text-[14px] leading-snug ring-1 ring-ink-200 ring-inset'
              : 'text-[13px]',
          )}
        >
          {canEdit
            ? 'No services on this job yet. Add the work being done and each one gets its own report.'
            : 'No services on this job yet.'}
          {job.checklists && job.checklists.length > 0
            ? ' What ServiceM8 recorded is in the checklist below.'
            : ''}
        </p>
      ) : (
        <ul className={cn('flex flex-col', field ? 'gap-2.5' : 'gap-2')}>
          {services.map((service) => (
            <li key={service.id}>
              <ServiceRow
                service={service}
                open={open === service.id}
                canEdit={canEdit}
                isAddingNote={isAddingNote}
                field={field}
                onToggle={() => setOpen((current) => (current === service.id ? null : service.id))}
                onAddNote={(body, finished) => onAddNote(service.id, body, finished)}
                onRemove={() => onRemoveService(service.id)}
              />
            </li>
          ))}
        </ul>
      )}

      <AddServiceSheet
        open={adding}
        jobId={job.id}
        already={services.map((service) => service.serviceType.id)}
        onClose={() => setAdding(false)}
      />
    </section>
  )
}

/**
 * One service, collapsed to its state or open to its brief.
 *
 * The tone belongs to the service type and is carried by the tile, in one of
 * the brand's five tones (`services.ts`); the row itself is white, open or
 * shut, so an open row reads as the same object as the collapsed ones beside
 * it rather than as a page.
 *
 * The brief's panels are white so that the accents inside them mean what they
 * say: a red ticket banner or a red hazard has to land on paper.
 */
function ServiceRow({
  service,
  open,
  canEdit,
  isAddingNote,
  onToggle,
  onAddNote,
  onRemove,
  field,
}: {
  service: JobServiceRow
  open: boolean
  canEdit: boolean
  isAddingNote: boolean
  field: boolean
  onToggle: () => void
  onAddNote: (body: string, done: () => void) => void
  onRemove: () => void
}) {
  const { openReport } = useJobOverlay()
  const status = serviceStatus(service)
  const tone = serviceTone(service.serviceType.tone)
  const report = service.serviceType.formId !== null

  return (
    <div
      className={cn(
        'overflow-hidden ring-inset',
        field
          ? 'rounded-lg bg-white ring-1 ring-ink-200'
          : cn('rounded-lg bg-white text-ink-900 ring-1', open ? 'ring-ink-300' : 'ring-ink-200'),
      )}
    >
      {field ? (
        <div className={cn('flex items-center gap-3 py-3 pr-3.5 pl-3.5', open && 'border-b border-ink-100')}>
          <button
            type="button"
            aria-expanded={open}
            onClick={onToggle}
            className="flex min-w-0 flex-1 items-center gap-3 text-left"
          >
            <span
              className={cn('grid size-11 shrink-0 place-items-center rounded-lg ring-1 ring-inset', toneStyles[tone])}
            >
              <Icon name={serviceIcon(service.serviceType.icon)} className="size-6" />
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-base font-semibold text-ink-900">
                {service.serviceType.name}
              </span>
              <span
                className={cn('truncate text-[13px]', status.done ? 'text-brand-700' : 'text-ink-500')}
              >
                {status.label}
              </span>
            </span>
          </button>
          {report ? (
            <button
              type="button"
              onClick={() => openReport(service.id)}
              className={cn(
                'inline-flex h-8 shrink-0 items-center rounded-md px-3 text-[13px] font-semibold',
                status.done
                  ? 'bg-brand-100 text-brand-800 ring-1 ring-brand-200 ring-inset'
                  : 'bg-ink-900 text-white active:bg-ink-800',
              )}
            >
              {fieldReportAction(service)}
            </button>
          ) : status.done ? (
            <Icon name="check" className="size-5 shrink-0 text-brand-700" strokeWidth={2.4} />
          ) : null}
        </div>
      ) : (
        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-ink-50',
            open && 'rounded-b-none border-b border-ink-100',
          )}
        >
          <span
            className={cn(
              'grid size-9 shrink-0 place-items-center rounded-lg text-white',
              serviceTile(tone),
            )}
          >
            <Icon name={serviceIcon(service.serviceType.icon)} className="size-[18px]" />
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-semibold">{service.serviceType.name}</span>
            <span className="truncate text-xs text-ink-500">{status.label}</span>
          </span>
          <span className="ml-auto flex shrink-0 items-center gap-2">
            {status.done ? (
              <span className="inline-flex items-center gap-1.5 rounded-sm bg-brand-100 px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase text-brand-800 ring-1 ring-brand-200 ring-inset">
                <Icon name="check" className="size-2.5" strokeWidth={3} />
                Done
              </span>
            ) : null}
            <Icon
              name="forward"
              className={cn('size-4 opacity-60 transition-transform', open && 'rotate-90')}
            />
          </span>
        </button>
      )}

      {open ? (
        <div className={cn('flex flex-col gap-3 px-3 pt-3 pb-3.5', field && 'bg-ink-50 text-ink-800')}>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <p className="min-w-0 flex-1 text-[12.5px] leading-relaxed opacity-80">
              {service.scope ?? 'Nobody has written down what this service covers on this job.'}
            </p>
            {service.priceIncTax === null ? null : (
              <span className="text-[13.5px] font-bold tabular-nums">
                {money(service.priceIncTax)}
              </span>
            )}
          </div>

          <Brief jobServiceId={service.id} />

          <ServiceNotes
            service={service}
            canEdit={canEdit}
            isAdding={isAddingNote}
            onAddNote={onAddNote}
          />

          <div className="flex flex-wrap items-center gap-2">
            {service.serviceType.formId === null ? (
              <p className="text-[12.5px] opacity-70">
                This service does not produce a report. Anything worth keeping goes in a note.
              </p>
            ) : (
              <Button variant="primary" onClick={() => openReport(service.id)}>
                {status.action}
              </Button>
            )}
            {canEdit ? (
              <button
                type="button"
                onClick={onRemove}
                className="ml-auto text-xs font-medium opacity-70 hover:text-red-700 hover:opacity-100"
              >
                Take this service off the job
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

/**
 * One part of the brief: a quiet eyebrow over its content on paper.
 */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[10px] font-bold tracking-[0.06em] uppercase opacity-65">{title}</span>
      <div className="rounded-md bg-white px-3 py-2.5 ring-1 ring-ink-200 ring-inset">
        {children}
      </div>
    </div>
  )
}

/**
 * The three things a technician wants before they get out of the ute.
 *
 * One query, three blocks: what the last few people to do this work *at this
 * address* wrote down, what can hurt somebody doing it, and whether the person
 * reading the screen is allowed to. The server scopes all three — history to this
 * service type at this site, hazards to the service type, tickets to the caller
 * — because none of that is the client's business to filter.
 *
 * Fetched when the row opens rather than with the job. Five collapsed rows would
 * otherwise pay for five site-wide note searches to draw five lines of text.
 */
function Brief({ jobServiceId }: { jobServiceId: string }) {
  const trpc = useTRPC()
  const { data, isPending, error } = useQuery(
    trpc.jobs.serviceBrief.queryOptions({ jobServiceId }),
  )

  if (isPending) return <Spinner className="size-4 text-ink-400" />
  if (error) return <p className="text-[12.5px] text-red-700">{errorMessage(error)}</p>

  return (
    <>
      <PreviousVisits history={data.history} />
      <Safety hazards={data.hazards} siteHazardNotes={data.siteHazardNotes} />
      <Tickets tickets={data.tickets} />
    </>
  )
}

/**
 * What the last people to do this work here wrote down.
 *
 * The question a technician has at the gate, and the one that used to mean
 * opening three closed jobs.
 */
function PreviousVisits({ history }: { history: BriefHistory }) {
  return (
    <Section title="Notes from the last visits">
      {history.length === 0 ? (
        <p className="text-[12.5px] text-ink-500">
          Nothing written against this service at this address before. This is the first one.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-ink-100">
          {history.map((note) => (
            <li key={note.id} className="py-2 first:pt-0 last:pb-0">
              <div className="flex items-baseline gap-2">
                <span className="text-[12px] font-semibold text-ink-800">
                  {note.author?.name ?? 'Someone'}
                </span>
                <span className="ml-auto text-[11px] text-ink-500">
                  {note.job?.completedAt ? formatDate(note.job.completedAt) : formatDate(note.createdAt)}
                </span>
              </div>
              <p className="mt-1 text-[13px] leading-relaxed whitespace-pre-wrap text-ink-700">
                {note.body}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

/**
 * What can hurt somebody doing this work, and what to do about it.
 *
 * Two sources, kept apart on purpose. The hazards belong to the kind of work — a
 * subfloor is a confined space everywhere — and the site's own notes belong to
 * the address, which is where the kelpie and the locked side gate live. Merging
 * them would mean either retyping the confined-space line at four thousand
 * addresses or losing the dog.
 *
 * The critical ones come first, from the server. There are only two levels
 * because a third is where everything becomes "high" and the word stops meaning
 * anything.
 */
function Safety({
  hazards,
  siteHazardNotes,
}: {
  hazards: BriefHazards
  siteHazardNotes: string | null
}) {
  if (hazards.length === 0 && siteHazardNotes === null) return null

  return (
    <Section title="Safety on this service">
      {hazards.length > 0 ? (
        <ul className="flex flex-col gap-2.5">
          {hazards.map((hazard) => (
            <li key={hazard.id} className="flex gap-2.5">
              <span
                aria-hidden
                className={cn(
                  'mt-[7px] size-[7px] shrink-0 rounded-[2px]',
                  hazard.severity === 'CRITICAL' ? 'bg-red-600' : 'bg-red-400',
                )}
              />
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-[13px] font-semibold text-ink-800">
                  {hazard.title}
                  {hazard.severity === 'CRITICAL' ? (
                    <span className="ml-2 text-[10px] font-bold tracking-wide uppercase text-red-700">
                      Critical
                    </span>
                  ) : null}
                </span>
                <span className="text-[12.5px] leading-relaxed text-ink-600">{hazard.control}</span>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {siteHazardNotes === null ? null : (
        <div
          className={cn(
            'rounded-md bg-red-50 px-3 py-2.5 ring-1 ring-red-200 ring-inset',
            hazards.length > 0 && 'mt-2.5',
          )}
        >
          <div className="flex items-center gap-1.5">
            <Icon name="alert" className="size-3.5 text-red-700" />
            <span className="text-[11px] font-bold tracking-wide uppercase text-red-700">
              At this address
            </span>
          </div>
          <p className="mt-1 text-[13px] leading-relaxed whitespace-pre-wrap text-ink-800">
            {siteHazardNotes}
          </p>
        </div>
      )}
    </Section>
  )
}

/**
 * Whether the person reading this is allowed to do the work.
 *
 * The caller's own holdings and nobody else's, which is a deliberate limit: a
 * technician looking at their own gaps is a prompt to renew, and looking at a
 * colleague's is a payroll matter that does not belong on a job card.
 *
 * A blocking ticket is drawn as a banner rather than a badge in a list, because
 * the whole point is that it is read before the work starts rather than after.
 * It does not disable the report button. Somebody standing at a gate with a
 * lapsed licence needs to be told, not locked out of the only screen that would
 * let them write down why they left.
 */
function Tickets({ tickets }: { tickets: BriefTickets }) {
  if (tickets.length === 0) return null

  const blocking = tickets.filter((ticket) => ticket.blocking)

  return (
    <Section title="Your tickets for this work">
      {blocking.length > 0 ? (
        <div
          role="alert"
          className="mb-2.5 rounded-md bg-red-50 px-3 py-2.5 ring-1 ring-red-200 ring-inset"
        >
          <div className="flex items-center gap-1.5">
            <Icon name="shield" className="size-3.5 text-red-700" />
            <span className="text-[11px] font-bold tracking-wide uppercase text-red-700">
              {blocking.length === 1 ? 'A ticket this work needs' : 'Tickets this work needs'}
            </span>
          </div>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-800">
            {blocking.map((ticket) => ticket.licenceType.name).join(', ')}
            {blocking.length === 1 ? ' is not current for you.' : ' are not current for you.'} Ring
            the office before you start.
          </p>
        </div>
      ) : null}

      <ul className="flex flex-col divide-y divide-ink-100">
        {tickets.map((ticket) => {
          const badge = ticketBadge(ticket.standing)

          return (
            <li key={ticket.id} className="flex items-center gap-2.5 py-2 first:pt-0 last:pb-0">
              <Icon name="card" className="size-4 shrink-0 text-ink-400" />
              <div className="flex min-w-0 flex-col gap-px">
                <span className="truncate text-[13px] font-semibold text-ink-800">
                  {ticket.licenceType.name}
                  {ticket.isMandatory ? null : (
                    <span className="ml-2 text-[10px] font-medium text-ink-500">Worth having</span>
                  )}
                </span>
                <span className="text-[11.5px] text-ink-500">{ticketDetail(ticket)}</span>
              </div>
              <span
                className={cn(
                  'ml-auto shrink-0 rounded-sm px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase ring-1 ring-inset',
                  toneStyles[badge.tone],
                )}
              >
                {badge.label}
              </span>
            </li>
          )
        })}
      </ul>
    </Section>
  )
}

/** Notes against this service on this job, and adding one. */
function ServiceNotes({
  service,
  canEdit,
  isAdding,
  onAddNote,
}: {
  service: JobServiceRow
  canEdit: boolean
  isAdding: boolean
  onAddNote: (body: string, done: () => void) => void
}) {
  const [draft, setDraft] = useState('')

  return (
    <div className="flex flex-col gap-2">
      <Section title="Notes on this service">
        {service.notes.length === 0 ? (
          <p className="text-[12.5px] text-ink-500">Nothing noted against this service yet.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-ink-100">
            {service.notes.map((note) => (
              <li key={note.id} className="py-2 first:pt-0 last:pb-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-[12px] font-semibold text-ink-800">
                    {note.author?.name ?? 'Someone'}
                  </span>
                  <span className="ml-auto text-[11px] text-ink-500">
                    {formatDateTime(note.createdAt)}
                  </span>
                </div>
                <p className="mt-1 text-[13px] leading-relaxed whitespace-pre-wrap text-ink-700">
                  {note.body}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {canEdit ? (
        <form
          className="flex flex-col gap-1.5"
          onSubmit={(event) => {
            event.preventDefault()
            const body = draft.trim()
            if (body.length === 0) return
            onAddNote(body, () => setDraft(''))
          }}
        >
          <Textarea
            aria-label={`Note about ${service.serviceType.name}`}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="What should the office or the next technician know about this service?"
            className="min-h-16 text-[13px]"
          />
          <div className="flex justify-end">
            <Button type="submit" loading={isAdding} disabled={draft.trim().length === 0}>
              Add the note
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  )
}

/** The button on a field row: short, because it sits beside the name. */
function fieldReportAction(service: JobServiceRow): string {
  if (service.formResponse === null) return 'Start report'
  if (service.formResponse.status === 'DRAFT') return 'Continue'
  return 'View'
}
